import { PetAvatar } from 'components/Pet/Avatar/PetAvatar';
import { PetTypeChip } from 'components/Pet/TypeChip/PetTypeChip';
import { useMarketplace } from 'hooks/useMarketplace';
import React from 'react';
import { useSelector } from 'react-redux';
import {
    getOnlineBookingState,
    getOnlineBookingTotalAmount,
    getOnlineBookingTotalDeposit
} from 'reducers/onlineBooking/OnlineBookingSelectors';
import { OnlineBookingPricingOptions } from 'types/OnlineBooking';

import classes from './OnlineBookingResume.module.scss';

export const OnlineBookingResume: React.FC = () => {
    const marketplace = useMarketplace();

    const { date, time, bookingItems } = useSelector(getOnlineBookingState);

    const subtotal = useSelector(getOnlineBookingTotalAmount);
    const totalDeposit = useSelector(getOnlineBookingTotalDeposit);

    const total = React.useMemo(() => {
        return subtotal - totalDeposit;
    }, [subtotal, totalDeposit]);

    const bookingHasServices = React.useMemo(() => {
        return bookingItems?.length > 0;
    }, [bookingItems]);

    const depositEnabled = React.useMemo(() => {
        return (
            marketplace.deposit &&
            totalDeposit > 0 &&
            !marketplace.onlineBookingPreferences.requireBookingRequest
        );
    }, [
        marketplace.deposit,
        totalDeposit,
        marketplace.onlineBookingPreferences.requireBookingRequest
    ]);

    return (
        <div className={classes.root}>
            <div className={classes.header}>
                <h1 className={classes.title}>Booking details</h1>

                <span className={classes.date}>{date?.format('DD MMMM YYYY')}</span>
            </div>

            {bookingItems.length > 0 && (
                <div className={classes.bookingItems}>
                    {bookingItems.map(item => (
                        <div key={item.pet.uuid} className={classes.bookingItem}>
                            <PetAvatar size={56} petType={item.pet.type} />

                            <div className={classes.bookingItemInfo}>
                                <p className={classes.petName}>
                                    {item.pet.name}
                                    <PetTypeChip petType={item.pet.type} />
                                </p>
                                <p className={classes.petInfo}>
                                    {item.pet.breed?.name ?? 'Not Defined'} - {item.pet.size.name}
                                </p>
                                {item.service && (
                                    <p className={classes.itemLine}>{item.service.name}</p>
                                )}
                                {time && (
                                    <p className={classes.itemLine}>
                                        <mark>Time</mark> {time.label}
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {bookingHasServices && (
                <>
                    <div className={classes.total}>
                        <p>
                            {marketplace.onlineBookingPreferences.pricingOption ===
                            OnlineBookingPricingOptions.StartingAt
                                ? 'Starting at'
                                : 'Price'}
                        </p>

                        <p>${subtotal.toFixed(2)}</p>
                    </div>

                    {depositEnabled && (
                        <div className={classes.deposit}>
                            <div>
                                <p>Deposit to book</p>
                                <p>${totalDeposit.toFixed(2)}</p>
                            </div>

                            <div>
                                <p>Estimated total due at appointment</p>
                                <p>${total.toFixed(2)}</p>
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
